import { ArrearsState } from '../types';
import { format } from 'date-fns';
import { dateFormats } from 'utils/Dates';

/**
 * Get list of months that have been edited (not deleted)
 */
export const getEditedMonths = (state: ArrearsState): string[] => {
  return Object.keys(state.editHistory).filter((arrearId) => {
    const history = state.editHistory[arrearId];
    return history.fieldsChanged.size > 0 && !history.isDeleted && !history.isDeletedFromDb;
  });
};

/**
 * Get list of months marked for deletion
 */
export const getDeletedMonths = (state: ArrearsState): string[] => {
  return Object.keys(state.editHistory).filter((arrearId) => {
    return state.editHistory[arrearId]?.isDeleted && !state.editHistory[arrearId]?.isDeletedFromDb;
  });
};

/**
 * Get sorted list of non-deleted months with their sourceMonth as Date objects
 */
export const getNonDeletedMonths = (
  state: ArrearsState,
): Array<{ arrearId: string; sourceMonth: Date | null }> => {
  return Object.keys(state.months)
    .map((arrearId) => {
      const sourceMonth = state.months[arrearId].sourceMonth;
      const dateObject = sourceMonth ? new Date(sourceMonth) : null;
      return {
        arrearId,
        sourceMonth: dateObject,
        isDeleted:
          state.editHistory[arrearId]?.isDeleted ||
          state.editHistory[arrearId]?.isDeletedFromDb ||
          false,
      };
    })
    .filter(({ isDeleted }) => !isDeleted)
    .sort(({ sourceMonth: sourceMonth1 }, { sourceMonth: sourceMonth2 }) => {
      if (!sourceMonth1) return 1;
      if (!sourceMonth2) return -1;
      return sourceMonth1.getTime() - sourceMonth2.getTime();
    });
};

/**
 * Get formatted month strings for already selected months (used to disable in dropdown)
 */
export const getSelectedMonths = (state: ArrearsState): string[] => {
  return getNonDeletedMonths(state).map(({ sourceMonth }) =>
    sourceMonth ? format(sourceMonth, dateFormats.yearMonth) : '',
  );
};

/**
 * Check if computation is needed (compliance arrears enabled and component edits exist)
 */
export const getIsComputationNeeded = (
  state: ArrearsState,
  isComplianceArrearsEnabled: boolean,
): boolean => {
  if (!isComplianceArrearsEnabled) {
    return false;
  }

  return state.previewCache?.requestId
    ? false
    : Object.keys(state.editHistory).some((arrearId) => {
        const history = state.editHistory[arrearId];
        return (
          !history.isDeleted &&
          !history.isDeletedFromDb &&
          (history.fieldsChanged.has('components') || history.fieldsChanged.has('month'))
        );
      });
};

/**
 * Get months that need to be sent in bulkPreview payload (edited but not deleted)
 */
export const getMonthsForPreview = (state: ArrearsState): string[] => {
  return Object.keys(state.editHistory).filter((arrearId) => {
    const history = state.editHistory[arrearId];
    // Include if there are edits and it's not deleted
    return history.fieldsChanged.size > 0 && !history.isDeleted && !history.isDeletedFromDb;
  });
};

/**
 * Check if there are any unsaved changes
 */
export const getHasUnsavedChanges = (state: ArrearsState): boolean => {
  return getEditedMonths(state).length > 0 || getDeletedMonths(state).length > 0;
};

/**
 * Get the total number of arrear components across all months
 */
export const getTotalComponentsCount = (state: ArrearsState): number => {
  return Object.values(state.months).reduce((total, month) => total + month.components.length, 0);
};
